import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';

import MainLoader from '../../../components/Loaders/MainLoader';
import Error from '../../../components/errors/Error';
import DocumentCard from '../../../components/matriculations/DocumentCard';
import ContratoViewer from '../../../components/matriculations/ContratoViewer';
import BreadTwoParts from '../../../components/breadcrumbs/BreadTwoParts';
import ContractCard from '../../../components/matriculations/ContractCard';
import FormCard from '../../../components/matriculations/FormCard';
import FormViewer from '../../../components/matriculations/FormViewer';
import CompletionChecklist from '../../../components/matriculations/CompletionChecklist';
import { Toast } from '../../../resources/utils';
import { FaExclamationCircle, FaUpload } from 'react-icons/fa';

import PublicLayout from '../../../layouts/PublicLayout';
import { getAllProcesMatriculationThunk, initialState, subirDocumentosThunk } from '../../../store/slices/public/procesoMatriculacion.slice';
import { getStudentThunk } from '../../../store/slices/catalogs/students.slice';
import { getConfigsThunk } from '../../../store/slices/catalogs/config.slice';
import { getParentThunk } from '../../../store/slices/public/parents.slice';
import { formatMonthYear } from '../../../utils/funtions';
import { getCoursesThunk } from '../../../store/slices/catalogs/courses.slice';

const checkIfAllCompleted = (items) => {
    const allContractsSigned = !items?.contracts?.length || items.contracts.every(c => c.siggned);
    const allDocsUploaded = !items?.documents?.length || items.documents.every(d => d.respuesta);
    const allFormsSubmitted = !items?.forms?.length || items.forms.every(f => f.isSubmited);

    return allContractsSigned && allDocsUploaded && allFormsSubmitted;
};

const ProcesoMatriculacion = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { ci_estudiante } = useParams();
    const user = JSON.parse(localStorage.getItem("userInfo"));

    const configurationsState = useSelector(state => state.configurations);
    const representanteState = useSelector(state => state.parents);
    const { items, message, error } = useSelector(state => state.proceso_matriculacion);
    const { item } = useSelector(state => state.students);
    const courseState = useSelector(state => state.courses);
    const isLoading = useSelector(state => state.isLoadingSlice);

    const [activeStep, setActiveStep] = useState(0);
    const [activeUploadDocs, setActiveUploadDocs] = useState(false);
    const [selectedContrato, setSelectedContrato] = useState(null);
    const [selectedFormulario, setSelectedFormulario] = useState(null);
    const [viewerFormOpen, setViewerFormOpen] = useState(false);
    const [viewerOpen, setViewerOpen] = useState(false);
    const [dataContrat, setDataContract] = useState({});
    const [files, setFiles] = useState([]);
    const [showChecklist, setShowChecklist] = useState(false);

    const today = new Date();

    useEffect(() => {
        dispatch(getAllProcesMatriculationThunk(ci_estudiante));
        dispatch(getStudentThunk(ci_estudiante));
        dispatch(getParentThunk(user.id));
        dispatch(getConfigsThunk());
        dispatch(getCoursesThunk());
        return () => {
            dispatch(initialState());
        };
    }, [ci_estudiante]);

    useEffect(() => {
        if (courseState.items.length > 0) {
            const nextCourse = courseState?.items.find((course) => course.level === (item?.course?.level + 1))?.name
            setDataContract({
                today: formatMonthYear(today),
                nombre_representante: representanteState.item.nombre + ' ' + representanteState.item.apellido,
                cedula_representante: representanteState.item.ci_padre,
                correo_representante: representanteState.item.email,
                celular_representante: representanteState.item.celular,
                direccion_representante: representanteState.item.direccion,
                parentesco_representante: representanteState.item.parentesco,
                nacionalidad_representante: representanteState.item.nacionalidad,
                nombre_estudiante: item.nombre + ' ' + item.apellido,
                cedula_estudiante: item.ci_estudiante,
                curso_seccion: nextCourse,
                rector_colegio: configurationsState.items[0]?.director,
                nombre_colegio: configurationsState.items[0]?.name,
                RUC: configurationsState.items[0]?.phone,
                siguiente_periodo: formatMonthYear(configurationsState.items[0]?.fecha_inicio_periodo_siguiente) + ' - ' + formatMonthYear(configurationsState.items[0]?.fecha_fin_periodo_siguiente),
            });
        }
    }, [item, configurationsState, courseState]);

    useEffect(() => {
        if (items) {
            const documents = items.documents?.filter(document => !document.respuesta)
            setActiveUploadDocs(Object.keys(files).length < documents?.length);
        }
    }, [items, files]);

    useEffect(() => {
        if (!message) return;

        if (message === 'Proceso de matriculacion culminado correctamente') {
            Toast.fire({ icon: 'success', title: message });
            navigate('/inicio');
        } else {
            const ignoredMessages = ['Contrato firmado exitosamente', 'Formulario enviado correctamente'];
            if (!ignoredMessages.includes(message)) {
                Toast.fire({ icon: 'success', title: message });
            }

            handleCloseViewer();
            handleCloseViewerForm();
            dispatch(getAllProcesMatriculationThunk(ci_estudiante));
        }
    }, [message]);

    useEffect(() => {
        if (!items) return;

        const isReady = items.contracts || items.forms || items.documents;
        const isCompleted = checkIfAllCompleted(items);

        if (isReady && isCompleted) {
            setShowChecklist(true);
        } else {
            setShowChecklist(false);
        }
    }, [items]);

    const steps = [];
    if (items?.contracts?.length > 0) steps.push({ label: "Contratos", type: "contracts" });
    if (items?.documents?.length > 0) steps.push({ label: "Documentos Habilitantes", type: "documents" });
    if (items?.forms?.length > 0) steps.push({ label: "Formularios", type: "forms" });

    const nextStep = () => {
        if (activeStep < steps.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const prevStep = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    const handleView = (item) => {
        setSelectedContrato(item);
        setViewerOpen(true);
    };

    const handleCloseViewer = () => {
        setViewerOpen(false);
        setSelectedContrato(null);
    };

    const handleViewForm = (item) => {
        setViewerFormOpen(true);
        setSelectedFormulario(item);
    };

    const handleCloseViewerForm = () => {
        setViewerFormOpen(false);
        setSelectedFormulario(null);
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        if (files && files.length > 0) {
            setFiles((prev) => ({ ...prev, [name]: files[0] }));
        }
    };

    const handleCargaDocumentos = () => {
        const formData = new FormData();
        for (let key in files) {
            formData.append(`documents`, files[key]);
            formData.append(`documentId`, key);
        }
        dispatch(subirDocumentosThunk(ci_estudiante, user.id, formData));
    };

    const renderStepContent = (type) => {
        switch (type) {
            case 'contracts':
                return items.contracts.map(item => (
                    <ContractCard
                        key={item.id}
                        title={item.contract?.name}
                        isSigned={item.siggned}
                        onView={() => handleView(item)}
                    />
                ));
            case 'documents':
                return (
                    <>
                        {items.documents.map(item => (
                            <DocumentCard
                                key={item.id}
                                title={item.documento?.name}
                                document={item}
                                isUpload={item.respuesta}
                                files={files}
                                handleFileChange={handleFileChange}
                            />
                        ))}
                        <button
                            onClick={handleCargaDocumentos}
                            disabled={activeUploadDocs}
                            className={`w-full mt-10 ${activeUploadDocs ? 'bg-gray-400' : 'bg-p-sky'} text-white font-semibold py-2 px-4 rounded-lg flex items-center justify-center`}
                        >
                            <FaUpload className="mr-2" />
                            Subir Documentos
                        </button>
                    </>
                );
            case 'forms':
                return items.forms.map(item => (
                    <FormCard
                        key={item.id}
                        title={item.formulario?.titulo}
                        isSigned={item.isSubmited}
                        onView={() => handleViewForm(item)}
                    />
                ));
            default:
                return null;
        }
    };

    return (
        <PublicLayout>
            {isLoading || !items ? (
                <MainLoader />
            ) : (
                showChecklist ? (
                    <div className="w-full text-green-800 p-4 rounded-lg text-center font-bold flex items-center justify-center">
                        <CompletionChecklist ci_padre={dataContrat.cedula_representante} />
                    </div>
                ) : (
                    <div className='w-full m-5'>
                        {error && <Error state={error} />}
                        <BreadTwoParts
                            titleOne={t("Inicio")} toOne={"/inicio"}
                            titleTwo={"Proceso de Matriculación" + ' > ' + item.nombre + ' ' + item.apellido}
                        />

                        <div className="mb-8">
                            <ol className="flex space-x-4">
                                {steps.map((step, index) => (
                                    <li
                                        key={index}
                                        className={`flex-1 px-4 py-2 text-center border-b-4 uppercase ${index === activeStep
                                            ? "border-p-green font-bold"
                                            : "border-gray-300 text-gray-500"
                                            }`}
                                    >
                                        {step.label}
                                    </li>
                                ))}
                            </ol>
                        </div>
                        <div className="bg-white shadow-md p-3 rounded-md h-[70%]">
                            <div className="text-start h-[95%] overflow-y-auto border border-gray-100 rounded-lg p-2 text-sm text-gray-700 bg-gray-50">
                                {renderStepContent(steps[activeStep]?.type)}
                            </div>
                        </div>
                        {steps.length > 1 && (
                            <div className="mt-3 flex justify-between">
                                <button
                                    onClick={prevStep}
                                    disabled={activeStep === 0}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded disabled:opacity-50"
                                >
                                    Anterior
                                </button>
                                <div className='flex items-center gap-2 border border-warning/50 bg-warning/10 p-3 rounded-lg animate-pulse'>
                                    <FaExclamationCircle size={20} className='text-warning' />
                                    <h2 className="text-lg">Revise, acepte y firme todos los contratos, documentos o formularios para completar el proceso.</h2>
                                </div>
                                <button
                                    onClick={nextStep}
                                    disabled={activeStep === steps.length - 1}
                                    className="px-4 py-2 bg-blue-600 text-white rounded disabled:opacity-50"
                                >
                                    Siguiente
                                </button>
                            </div>
                        )}
                        <ContratoViewer
                            isOpen={viewerOpen}
                            onClose={handleCloseViewer}
                            contrato={selectedContrato?.contract}
                            contratoId={selectedContrato?.id}
                            datosUsuario={dataContrat}
                        />
                        <FormViewer
                            isOpen={viewerFormOpen}
                            onClose={handleCloseViewerForm}
                            formulario={selectedFormulario?.formulario}
                            formularioId={selectedFormulario?.id}
                        />
                    </div>
                )
            )}
        </PublicLayout>
    );
};

export default ProcesoMatriculacion;